import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Shield,
  ShieldCheck,
  Pencil,
  Trash2,
  KeyRound,
  MoreHorizontal,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import {
  useRoles,
  useAllPermissions,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useSetRolePermissions,
} from "../api";
import { usePermissions as useAuthPermissions } from "@/stores";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Textarea,
} from "@/components/ui";
import {
  EmptyState,
  PageError,
  PageHeader,
  PageLoader,
  DataTable,
  type Column,
} from "@/components/common";
import type {
  CreateRoleRequest,
  Permission,
  Role,
  UpdateRoleRequest,
} from "@/types";

const BUILT_IN_ROLES = new Set(["admin", "manager", "barista", "cashier"]);

const DEFAULT_PERMISSION_SLUGS = new Set([
  "categories.read",
  "products.read",
  "attributes.read",
  "customers.read",
  "customers.create",
  "orders.read",
  "orders.create",
  "orders.update",
  "orders.pay",
  "shifts.read",
  "shifts.manage",
  "inventory.read",
  "timeclock.manage",
  "favorites.read",
  "favorites.manage",
]);

const roleBadgeVariants: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  admin: "destructive",
  manager: "default",
  barista: "secondary",
  cashier: "outline",
};

export function RolesPage() {
  const { hasPermission } = useAuthPermissions();
  const canCreate = hasPermission("roles.create");
  const canUpdate = hasPermission("roles.update");
  const canDelete = hasPermission("roles.delete");

  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [permissionsRole, setPermissionsRole] = useState<Role | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  const { data: roles, isLoading, error, refetch } = useRoles(true);
  const { data: permissions, isLoading: isPermissionsLoading } =
    useAllPermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const setRolePermissions = useSetRolePermissions();

  const filteredRoles = useMemo(() => {
    if (!roles) return [];
    const q = search.trim().toLowerCase();
    if (!q) return roles;

    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q),
    );
  }, [roles, search]);

  const handleCreate = async (data: CreateRoleRequest) => {
    try {
      await createRole.mutateAsync(data);
      toast.success("Role created successfully");
      setIsCreateOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create role");
    }
  };

  const handleUpdate = async (data: UpdateRoleRequest) => {
    if (!editRole) return;
    try {
      await updateRole.mutateAsync({ id: editRole.id, data });
      toast.success("Role updated successfully");
      setEditRole(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update role");
    }
  };

  const handleSavePermissions = async (permissionIds: string[]) => {
    if (!permissionsRole) return;
    try {
      await setRolePermissions.mutateAsync({
        id: permissionsRole.id,
        data: { permissionIds },
      });
      toast.success("Permissions updated successfully");
      setPermissionsRole(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update permissions",
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRole.mutateAsync(deleteTarget.id);
      toast.success("Role deleted successfully");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete role");
    }
  };

  const columns: Column<Role>[] = [
    {
      key: "role",
      header: "Role",
      cell: (role) => (
        <Badge variant={roleBadgeVariants[role.name] ?? "secondary"}>
          {role.name}
        </Badge>
      ),
    },
    {
      key: "description",
      header: "Description",
      cell: (role) => (
        <span className="max-w-md text-sm text-muted-foreground">
          {role.description || <span className="italic">No description</span>}
        </span>
      ),
    },
    {
      key: "permissions",
      header: "Permissions",
      className: "w-[140px]",
      cell: (role) => (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
          {role.permissions.length}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      className: "w-[100px]",
      cell: (role) => (
        <span className="text-xs text-muted-foreground">
          {BUILT_IN_ROLES.has(role.name) ? "Built-in" : "Custom"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "w-[100px]",
      cell: (role) => (
        <RoleActions
          role={role}
          isBuiltIn={BUILT_IN_ROLES.has(role.name)}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={() => setEditRole(role)}
          onManagePermissions={() => setPermissionsRole(role)}
          onDelete={() => setDeleteTarget(role)}
        />
      ),
    },
  ];

  const renderMobileCard = (role: Role) => {
    const isBuiltIn = BUILT_IN_ROLES.has(role.name);

    return (
      <div className="rounded-lg border p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={roleBadgeVariants[role.name] ?? "secondary"}>
                {role.name}
              </Badge>
              {isBuiltIn && (
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Built-in
                </span>
              )}
            </div>
            {role.description && (
              <p className="mt-2 text-sm text-muted-foreground">
                {role.description}
              </p>
            )}
          </div>
          <RoleActions
            role={role}
            isBuiltIn={isBuiltIn}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onEdit={() => setEditRole(role)}
            onManagePermissions={() => setPermissionsRole(role)}
            onDelete={() => setDeleteTarget(role)}
          />
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <KeyRound className="h-3.5 w-3.5" />
          {role.permissions.length} permission
          {role.permissions.length === 1 ? "" : "s"}
        </div>
      </div>
    );
  };

  if (isLoading && !roles) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load roles" onRetry={refetch} />;

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <PageHeader
        title="Roles & Permissions"
        description="Manage roles and control what each role can access"
        actions={
          canCreate && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Role
            </Button>
          )
        }
      />
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Roles ({roles?.length ?? 0})
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!filteredRoles.length ? (
            <EmptyState
              icon={Shield}
              title="No roles found"
              description={
                search
                  ? "Try adjusting your search"
                  : "Get started by adding a new role"
              }
              action={
                !search && canCreate ? (
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Role
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <DataTable
              data={filteredRoles}
              columns={columns}
              rowKey={(role) => role.id}
              renderMobileCard={renderMobileCard}
            />
          )}
        </CardContent>
      </Card>
      <RoleFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={(data) => handleCreate(data as CreateRoleRequest)}
        isLoading={createRole.isPending}
        title="Create Role"
      />
      {editRole && (
        <RoleFormDialog
          open={!!editRole}
          onOpenChange={(open) => !open && setEditRole(null)}
          role={editRole}
          onSubmit={(data) => handleUpdate(data as UpdateRoleRequest)}
          isLoading={updateRole.isPending}
          title="Edit Role"
        />
      )}

      {permissionsRole && (
        <PermissionsDialog
          open={!!permissionsRole}
          onOpenChange={(open) => !open && setPermissionsRole(null)}
          role={permissionsRole}
          permissions={permissions ?? []}
          isLoadingPermissions={isPermissionsLoading}
          onSubmit={handleSavePermissions}
          isLoading={setRolePermissions.isPending}
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the{" "}
              <strong>{deleteTarget?.name}</strong> role. Users assigned to this
              role must be moved to another role first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteRole.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRole.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface RoleActionsProps {
  role: Role;
  isBuiltIn: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onManagePermissions: () => void;
  onDelete: () => void;
}

function RoleActions({
  isBuiltIn,
  canUpdate,
  canDelete,
  onEdit,
  onManagePermissions,
  onDelete,
}: RoleActionsProps) {
  if (!canUpdate && !canDelete) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="shrink-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canUpdate && (
          <>
            <DropdownMenuItem onClick={onManagePermissions}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Manage permissions
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit details
            </DropdownMenuItem>
          </>
        )}
        {canDelete && !isBuiltIn && (
          <DropdownMenuItem
            onClick={onDelete}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: Role;
  onSubmit: (data: CreateRoleRequest | UpdateRoleRequest) => void;
  isLoading: boolean;
  title: string;
}

function RoleFormDialog({
  open,
  onOpenChange,
  role,
  onSubmit,
  isLoading,
  title,
}: RoleFormDialogProps) {
  const isBuiltIn = role ? BUILT_IN_ROLES.has(role.name) : false;
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");

  useEffect(() => {
    if (open) {
      setName(role?.name ?? "");
      setDescription(role?.description ?? "");
    }
  }, [open, role]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim().toLowerCase();
    const trimmedDescription = description.trim();

    if (role) {
      const data: UpdateRoleRequest = {
        description: trimmedDescription || null,
      };
      if (!isBuiltIn && trimmedName !== role.name) {
        data.name = trimmedName;
      }
      onSubmit(data);
    } else {
      onSubmit({
        name: trimmedName,
        description: trimmedDescription || null,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="roleName">Name</Label>
            <Input
              id="roleName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. supervisor"
              required
              disabled={isBuiltIn}
              pattern="^[a-z][a-z0-9_]*$"
              title="Lowercase letters, numbers, underscores; must start with a letter"
            />
            {isBuiltIn && (
              <p className="text-xs text-muted-foreground">
                Built-in role names cannot be changed.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="roleDescription">Description</Label>
            <Textarea
              id="roleDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What can this role do?"
              rows={3}
              maxLength={200}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface PermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role;
  permissions: Permission[];
  isLoadingPermissions: boolean;
  onSubmit: (permissionIds: string[]) => void;
  isLoading: boolean;
}

function PermissionsDialog({
  open,
  onOpenChange,
  role,
  permissions,
  isLoadingPermissions,
  onSubmit,
  isLoading,
}: PermissionsDialogProps) {
  const isProtectedRole = role.name === "admin";
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(role.permissions.map((p) => p.id)),
  );
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open) return;
    if (role.permissions.length > 0) {
      setSelected(new Set(role.permissions.map((p) => p.id)));
    } else {
      const defaults = new Set(
        permissions
          .filter((p) => DEFAULT_PERMISSION_SLUGS.has(p.slug))
          .map((p) => p.id),
      );
      setSelected(defaults);
    }
    setFilter("");
  }, [open, role, permissions]);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const map = new Map<string, Permission[]>();
    for (const perm of permissions) {
      if (
        q &&
        !perm.slug.toLowerCase().includes(q) &&
        !perm.module.toLowerCase().includes(q) &&
        !(perm.description ?? "").toLowerCase().includes(q)
      ) {
        continue;
      }
      const list = map.get(perm.module) ?? [];
      list.push(perm);
      map.set(perm.module, list);
    }

    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions, filter]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  };

  const toggleModule = (modulePerms: Permission[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = modulePerms.every((p) => next.has(p.id));
      if (allSelected) {
        modulePerms.forEach((p) => next.delete(p.id));
      } else {
        modulePerms.forEach((p) => next.add(p.id));
      }

      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit([...selected]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            Manage permissions ·{" "}
            <span className="font-mono text-base">{role.name}</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {isProtectedRole && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              The <strong>admin</strong> role has all permissions and cannot be
              edited.
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Filter permissions..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-8"
                disabled={isProtectedRole}
              />
            </div>
            <span className="shrink-0 text-sm text-muted-foreground">
              {selected.size} / {permissions.length} selected
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isProtectedRole || isLoadingPermissions}
              onClick={() => setSelected(new Set(permissions.map((p) => p.id)))}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isProtectedRole || isLoadingPermissions}
              onClick={() =>
                setSelected(
                  new Set(
                    permissions
                      .filter((p) => DEFAULT_PERMISSION_SLUGS.has(p.slug))
                      .map((p) => p.id),
                  ),
                )
              }
              title="Pre-select read-catalog / take-orders / start-shift permissions"
            >
              Apply defaults
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isProtectedRole || isLoadingPermissions}
              onClick={() => setSelected(new Set())}
            >
              Clear all
            </Button>
          </div>
          <div className="space-y-4 rounded-md border p-3">
            {isLoadingPermissions ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Loading permissions…
              </p>
            ) : grouped.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No permissions match your filter.
              </p>
            ) : (
              grouped.map(([module, modulePerms]) => {
                const allSelected = modulePerms.every((p) =>
                  selected.has(p.id),
                );
                const someSelected = modulePerms.some((p) =>
                  selected.has(p.id),
                );

                return (
                  <div key={module} className="space-y-2">
                    <div className="flex items-center justify-between gap-2 border-b pb-1.5">
                      <p className="text-sm font-semibold capitalize">
                        {module}
                      </p>
                      <button
                        type="button"
                        onClick={() => toggleModule(modulePerms)}
                        disabled={isProtectedRole}
                        className="text-xs text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {allSelected
                          ? "Clear all"
                          : someSelected
                            ? "Select all"
                            : "Select all"}
                      </button>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {modulePerms.map((perm) => {
                        const checked = selected.has(perm.id);

                        return (
                          <label
                            key={perm.id}
                            className="flex cursor-pointer items-start gap-2.5 rounded-md p-2 text-sm hover:bg-muted/50"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggle(perm.id)}
                              disabled={isProtectedRole}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-mono text-xs">{perm.slug}</p>
                              {perm.description && (
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  {perm.description}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || isProtectedRole}>
              {isLoading ? "Saving..." : "Save permissions"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
